import { createSlice } from '@reduxjs/toolkit';
import {
  AskAISignalHeaderType,
  AskAISignalItemType,
  AskAISignalModelOptionType,
  CellLevelApiCallTypeForSignalEnrichment,
} from './types';
import { DEFAULT_RESPONSE_FORMAT_OPTIONS, extractResponseFormatOptions } from './responseFormat';
import {
  bulkDeleteAskAiConfig,
  deleteAskAiConfig,
  deleteAskAiSignalConfig,
  getAskAiSignalData,
  getAskAISignalFields,
  getAskAiSignalModels,
  getTotalLibraryCount,
} from './ask-ai-signal.actions';
import { set } from 'lodash';

export interface AskAiSignalInitialState {
  askAiSignals: AskAISignalItemType[];
  aiModels: AskAISignalModelOptionType[];
  askAiSignalFields: {
    quickAccessHeaders: AskAISignalHeaderType[];
    accountHeaders: AskAISignalHeaderType[];
    contactHeaders: AskAISignalHeaderType[];
  };
  responseFormatOptions: string[];
  showCustomizeViewPopup?: boolean;
  requiredCreditForSignalEnrich: number;
  requiredCreditForAskAiEnrich: number;
  cellLevelApiCallForSignalEnrichment: CellLevelApiCallTypeForSignalEnrichment;
  cellLevelApiCallForAskAiEnrichment: CellLevelApiCallTypeForSignalEnrichment;
  newEnrichedAccountForSignalEnrichment: number[];
  newEnrichedAccountForAskAiEnrichment: number[];
  newEnrichedContactForAskAiEnrichment: number[];
  totalLibraryCount: number;
  pendingSignalOrderIds: string[];
  pendingAskAiOrderIds: string[];
}
const askAiSignalSlice = createSlice({
  name: 'askAiSignal',
  initialState: {
    askAiSignals: [],
    aiModels: [],
    askAiSignalFields: {
      quickAccessHeaders: [],
      accountHeaders: [],
      contactHeaders: [],
    },
    responseFormatOptions: [...DEFAULT_RESPONSE_FORMAT_OPTIONS],
    requiredCreditForSignalEnrich: 0,
    requiredCreditForAskAiEnrich: 0,
    cellLevelApiCallForSignalEnrichment: {},
    cellLevelApiCallForAskAiEnrichment: {},
    newEnrichedAccountForSignalEnrichment: [],
    newEnrichedAccountForAskAiEnrichment: [],
    newEnrichedContactForAskAiEnrichment: [],
    totalLibraryCount: 0,
    pendingSignalOrderIds: [],
    pendingAskAiOrderIds: [],
  } as AskAiSignalInitialState,
  reducers: {
    setCustomizeViewPopup: (state, action) => {
      state.showCustomizeViewPopup = action.payload;
    },
    setRequiredCreditForSignalEnrich: (state, action) => {
      state.requiredCreditForSignalEnrich =
        (state.requiredCreditForSignalEnrich || 0) + action.payload;
    },
    setRequiredCreditForAskAiEnrich: (state, action) => {
      state.requiredCreditForAskAiEnrich =
        (state.requiredCreditForAskAiEnrich || 0) + action.payload;
    },
    resetRequiredCreditForSignalEnrich: (state) => {
      state.requiredCreditForSignalEnrich = 0;
    },
    resetRequiredCreditForAskAiEnrich: (state) => {
      state.requiredCreditForAskAiEnrich = 0;
    },
    updateCellLevelApiCallForSignalEnrichment: (state, action) => {
      const { key, pk } = action.payload;
      if (!state.cellLevelApiCallForSignalEnrichment) {
        state.cellLevelApiCallForSignalEnrichment = {};
      }
      if (state.cellLevelApiCallForSignalEnrichment[key]) {
        if (!state.cellLevelApiCallForSignalEnrichment[key].includes(pk)) {
          state.cellLevelApiCallForSignalEnrichment[key].push(pk);
        }
      } else {
        state.cellLevelApiCallForSignalEnrichment[key] = [pk];
      }
    },
    updateCellLevelApiCallForAskAiEnrichment: (state, action) => {
      const { key, pk } = action.payload;
      if (!state.cellLevelApiCallForAskAiEnrichment) {
        state.cellLevelApiCallForAskAiEnrichment = {};
      }
      if (state.cellLevelApiCallForAskAiEnrichment[key]) {
        if (!state.cellLevelApiCallForAskAiEnrichment[key].includes(pk)) {
          state.cellLevelApiCallForAskAiEnrichment[key].push(pk);
        }
      } else {
        state.cellLevelApiCallForAskAiEnrichment[key] = [pk];
      }
    },
    setNewEnrichedAccountForSignalEnrichment: (state, action) => {
      state.newEnrichedAccountForSignalEnrichment = action.payload;
    },
    setNewEnrichedAccountForAskAiEnrichment: (state, action) => {
      if (action.payload.fresh) {
        state.newEnrichedAccountForAskAiEnrichment = [...action.payload.data];
      } else {
        state.newEnrichedAccountForAskAiEnrichment = [
          ...(state.newEnrichedAccountForAskAiEnrichment || []),
          ...action.payload.data,
        ];
      }
    },
    setNewEnrichedContactForAskAiEnrichment: (state, action) => {
      if (action.payload.fresh) {
        state.newEnrichedContactForAskAiEnrichment = [...action.payload.data];
      } else {
        state.newEnrichedContactForAskAiEnrichment = [
          ...state.newEnrichedContactForAskAiEnrichment,
          ...action.payload.data,
        ];
      }
    },
    clearCellLevelApiCallForSignalEnrichment: (state) => {
      state.cellLevelApiCallForSignalEnrichment = {};
    },
    clearCellLevelApiCallForAskAiEnrichment: (state) => {
      state.cellLevelApiCallForAskAiEnrichment = {};
    },
    addPendingSignalOrder: (state, { payload }: { payload: string }) => {
      if (!state.pendingSignalOrderIds.includes(payload)) {
        state.pendingSignalOrderIds.push(payload);
      }
    },
    removePendingSignalOrder: (state, { payload }: { payload: string }) => {
      state.pendingSignalOrderIds = state.pendingSignalOrderIds.filter((id) => id !== payload);
    },
    addPendingAskAiOrder: (state, { payload }: { payload: string }) => {
      if (!state.pendingAskAiOrderIds.includes(payload)) {
        state.pendingAskAiOrderIds.push(payload);
      }
    },
    removePendingAskAiOrder: (state, { payload }: { payload: string }) => {
      state.pendingAskAiOrderIds = state.pendingAskAiOrderIds.filter((id) => id !== payload);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAskAiSignalModels.pending, (state) => {
      state.aiModels = [];
    });
    builder.addCase(getAskAiSignalModels.fulfilled, (state, action) => {
      state.aiModels = action.payload;
    });
    builder.addCase(getAskAISignalFields.pending, (state) => {
      state.askAiSignalFields = {
        quickAccessHeaders: [],
        accountHeaders: [],
        contactHeaders: [],
      };
      state.responseFormatOptions = [...DEFAULT_RESPONSE_FORMAT_OPTIONS];
    });
    builder.addCase(getAskAISignalFields.fulfilled, (state, action) => {
      state.askAiSignalFields = {
        quickAccessHeaders: action.payload.quickAccessHeaders || [],
        accountHeaders: action.payload.accountHeaders,
        contactHeaders: action.payload.contactHeaders || [],
      };
      state.responseFormatOptions = extractResponseFormatOptions(action.payload);
    });
    builder.addCase(getAskAISignalFields.rejected, (state) => {
      state.askAiSignalFields = {
        quickAccessHeaders: [],
        accountHeaders: [],
        contactHeaders: [],
      };
      state.responseFormatOptions = [...DEFAULT_RESPONSE_FORMAT_OPTIONS];
    });
    builder.addCase(getAskAiSignalData.fulfilled, (state, action) => {
      state.askAiSignals = action.payload;
    });
    builder.addCase(getAskAiSignalData.rejected, (state) => {
      state.askAiSignals = [];
    });
    builder.addCase(deleteAskAiSignalConfig.fulfilled, (state, action) => {
      state.askAiSignals = state.askAiSignals.filter((s) => s.primaryKey !== action.payload);
    });
    builder.addCase(deleteAskAiConfig.fulfilled, (state, action) => {
      state.askAiSignals = state.askAiSignals.filter((s) => s.primaryKey !== action.payload.id);
    });
    builder.addCase(bulkDeleteAskAiConfig.fulfilled, (state, action) => {
      state.askAiSignals = state.askAiSignals.filter((s) => !action.payload.deleted.includes(s.primaryKey));
    });
    builder.addCase(getTotalLibraryCount.fulfilled, (state, action) => {
      state.totalLibraryCount = action.payload;
    });
  },
});

export const {
  setCustomizeViewPopup,
  setRequiredCreditForSignalEnrich,
  setRequiredCreditForAskAiEnrich,
  updateCellLevelApiCallForSignalEnrichment,
  updateCellLevelApiCallForAskAiEnrichment,
  setNewEnrichedAccountForSignalEnrichment,
  resetRequiredCreditForSignalEnrich,
  resetRequiredCreditForAskAiEnrich,
  clearCellLevelApiCallForSignalEnrichment,
  clearCellLevelApiCallForAskAiEnrichment,
  setNewEnrichedAccountForAskAiEnrichment,
  setNewEnrichedContactForAskAiEnrichment,
  addPendingSignalOrder,
  removePendingSignalOrder,
  addPendingAskAiOrder,
  removePendingAskAiOrder,
} = askAiSignalSlice.actions;

export default askAiSignalSlice.reducer;
